import { ReactNode } from 'react';
import {
  dateOperators,
  dateRangeOperators,
  globalOperators,
  numericOperators,
  Operator,
  recordListOperators,
  keyValueOperators,
  textOperators,
} from './operatorTypes';
import { Omit } from 'shared/utils';
import { Modifier } from './modifierTypes';

export type FieldTypes = Field | ListField | DateField | NumericField;
export type FieldTypeTypes =
  | 'text'
  | 'numeric'
  | 'date'
  | 'range'
  | 'list'
  | 'ip'
  | 'keyValue';

export interface Field {
  label?: string;
  prop: string;
  operators: Operator[];
  source: string;
  type: FieldTypeTypes;
  parentLabel?: string | null;
  removable?: boolean;
  query?: (value: string) => Promise<string[]>;
  propSeparator?: '.' | '_';
  // Custom value that will be displayed in the filter chip when the value is null.
  // By default this value is 'Empty'
  customNullValue?: string;
}

export interface ListFieldOption {
  type?: 'list';
  label: string;
  prop: string | null;
  group?: string;
  listTemplate?: ReactNode | ((name: string) => ReactNode);
  chipTemplate?: ReactNode | ((name: string) => ReactNode);
}

export interface NegatableListFieldOption extends ListFieldOption {
  negated: boolean;
}

export function isNegatableOptionsList(
  listFieldOptions?: ListFieldOption[] | NegatableListFieldOption[]
): listFieldOptions is NegatableListFieldOption[] {
  return !!listFieldOptions?.every(
    (o: ListFieldOption | NegatableListFieldOption) =>
      o.hasOwnProperty('negated')
  );
}

export interface NumericField extends Field {
  min?: number;
  max?: number;
  type: 'numeric';
}

export interface DateField extends Field {
  disableFuture?: boolean;
  disablePast?: boolean;
  modifiers?: Modifier[];
}

export interface ListField extends Field {
  options?: ListFieldOption[] | NegatableListFieldOption[];
}

export const booleanFieldDefaults: Omit<'source' | 'prop', ListField> = {
  type: 'list',
  operators: [
    {
      label: 'is',
      type: 'equals',
    },
  ],
  options: [
    {
      prop: 'true',
      label: 'True',
    },
    {
      prop: 'false',
      label: 'False',
    },
  ],
};

export const pastDateFieldDefaults: Omit<'source' | 'prop', DateField> = {
  operators: [
    ...globalOperators,
    ...dateRangeOperators.filter((d) => d.type !== 'next'),
    ...dateOperators,
  ],
  type: 'date',
  disableFuture: true,
};

export const ipFieldDefaults: Omit<'source' | 'prop', Field> = {
  operators: [...globalOperators],
  type: 'ip',
};

export const textFieldDefaults: Omit<'source' | 'prop', Field> = {
  operators: [...globalOperators, ...textOperators],
  type: 'text',
};

export const numericFieldDefaults: Omit<'source' | 'prop', NumericField> = {
  operators: [...globalOperators, ...numericOperators],
  type: 'numeric',
};

export const listFieldDefaults: Omit<'source' | 'prop', ListField> = {
  operators: [...globalOperators, ...recordListOperators],
  type: 'list',
};

export const keyValueFieldDefaults: Omit<'source' | 'prop', Field> = {
  operators: [...keyValueOperators],
  type: 'keyValue',
};
